import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowDown, ChevronDown, ChevronUp } from 'lucide-react';
import { Hero } from '../components/Hero';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { DayCard } from '../components/DayCard';
import { MetaphorCard } from '../components/MetaphorCard';
import { RecentPurchasePopup } from '../components/RecentPurchasePopup';
export function QuincenaEnVerdePage() {
  const CHECKOUT_URL = 'https://pay.hotmart.com/I103403400D';
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleCheckout = () => {
    window.location.href = CHECKOUT_URL;
  };
  const [showAllBonus1Features, setShowAllBonus1Features] = useState(false);
  const [showAllBonus2Features, setShowAllBonus2Features] = useState(false);
  const [showAllBonus3Features, setShowAllBonus3Features] = useState(false);
  const [showAllBonus4Features, setShowAllBonus4Features] = useState(false);
  const [showAllBonus5Features, setShowAllBonus5Features] = useState(false);
  return <main className="min-h-screen bg-[#FFF8F0] font-sans text-[#1B4332] selection:bg-[#F4A261] selection:text-[#1B4332]" data-id="element-81">
      <Hero data-id="element-82" />

      {/* Recent Purchase Popup */}
      <RecentPurchasePopup data-id="element-83" />

      {/* THE PROBLEM - EMOTIONAL CONNECTION */}
      <Section background="white" data-id="element-84">
        <div className="space-y-8 text-lg md:text-xl leading-relaxed text-gray-700" data-id="element-85">
          <p data-id="element-86">Porque no es solo el número. Es lo que viene detrás:</p>

          <ul className="space-y-3 pl-4 border-l-4 border-[#F4A261]" data-id="element-87">
            <li className="pl-4" data-id="element-88">improvisar otra vez</li>
            <li className="pl-4" data-id="element-89">apretarte a la fuerza</li>
            <li className="pl-4" data-id="element-90">decir "no puedo"</li>
            <li className="pl-4" data-id="element-91">aguantar ansiedad</li>
            <li className="pl-4" data-id="element-92">sentir culpa por "no organizarte"</li>
          </ul>

          <p className="font-medium text-[#1B4332]" data-id="element-93">
            Y aparece esa frase que ya te tiene harta:
          </p>
          <p className="text-2xl font-serif italic text-[#2D6A4F] text-center py-4" data-id="element-94">
            "Pero si yo casi no gasté..."
          </p>

          <p data-id="element-95">
            Si esa frase te suena... quédate conmigo. Porque lo que estás
            viviendo no es falta de disciplina.
          </p>
          <p data-id="element-96">Es algo más simple (y más peligroso):</p>
          <p className="font-bold text-[#1B4332]" data-id="element-97">
            Tu dinero no se va. Se mezcla. Y cuando se mezcla... se
            "desaparece".
          </p>
        </div>
      </Section>

      {/* THE REAL ENEMY - INVISIBLE EXPENSES */}
      <Section background="cream" data-id="element-98">
        <h2 className="text-3xl md:text-4xl font-bold text-[#1B4332] mb-8 text-center" data-id="element-99">
          El verdadero enemigo no es "un gasto grande"
        </h2>
        <p className="text-center text-xl text-gray-600 mb-12" data-id="element-100">
          Es la suma de 12 cositas que parecen inofensivas
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-101">
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100" data-id="element-102">
            <p className="mb-6 text-gray-700" data-id="element-103">
              La mayoría cree que el problema del dinero es un gasto enorme.
              Pero en la vida real (LATAM + hispana USA) el dinero se va por
              otro lado:
            </p>
            <ul className="space-y-3" data-id="element-104">
              {['solo un domicilio', 'solo la tiendita', 'solo un transporte', 'solo una suscripción', 'solo una cuota chiquita', 'solo un antojito', 'solo hoy'].map((item, i) => <li key={i} className="flex items-center gap-3 text-gray-600" data-id="element-105">
                  <X className="w-5 h-5 text-red-400" data-id="element-106" />
                  <span data-id="element-107">{item}</span>
                </li>)}
            </ul>
            <p className="mt-6 font-bold text-[#2D6A4F] text-center" data-id="element-108">
              Pequeño. Repetido. Invisible.
            </p>
          </div>

          <div className="space-y-6" data-id="element-109">
            <div className="bg-[#E8F5E9] p-6 rounded-xl border border-[#2D6A4F]/20" data-id="element-110">
              <h3 className="font-bold text-[#1B4332] mb-2" data-id="element-111">
                Mira un ejemplo sin Excel:
              </h3>
              <p className="text-sm text-gray-700 mb-4" data-id="element-112">
                Si se te van $6 diarios en "extras" (tiendita, snack, transporte
                extra), en 14 días son{' '}
                <span className="font-bold text-red-600" data-id="element-113">$84</span>.
              </p>
              <p className="text-sm text-gray-700" data-id="element-114">
                Y $84 no son "nada". Son la diferencia entre bajar una deuda o
                seguir igual.
              </p>
            </div>

            <p className="text-lg font-medium text-center" data-id="element-115">
              Ese dinero puede ser la diferencia entre:
            </p>

            <div className="flex flex-wrap justify-center gap-3" data-id="element-116">
              {['bajar una deuda', 'crear un mini colchón', 'respirar sin miedo'].map((tag, i) => <span key={i} className="px-4 py-2 bg-[#F4A261]/20 text-[#1B4332] rounded-full font-semibold text-sm" data-id="element-117">
                  {tag}
                </span>)}
            </div>
          </div>
        </div>
      </Section>

      {/* THE ROOT CAUSE */}
      <Section background="dark" className="text-center" data-id="element-118">
        <h2 className="text-3xl md:text-4xl font-bold text-[#F4A261] mb-6" data-id="element-119">
          La raíz que nadie te dijo (y te cambia el juego)
        </h2>
        <div className="max-w-2xl mx-auto space-y-6 text-lg text-white/90" data-id="element-120">
          <p data-id="element-121">No es que tú seas mala con el dinero.</p>
          <p className="text-2xl font-bold text-white" data-id="element-122">
            Es que tu dinero está mezclado.
          </p>
          <p data-id="element-123">
            Renta, comida, transporte, cuotas, "cositas", domicilios... todo
            sale del mismo lugar mental.
          </p>
          <p data-id="element-124">Y cuando todo sale del mismo lugar:</p>

          <div className="grid grid-cols-2 gap-4 text-left max-w-lg mx-auto mt-8" data-id="element-125">
            <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-126">
              <X className="w-6 h-6 text-red-300 mb-2" data-id="element-127" />
              <p data-id="element-128">tu cerebro no "siente" el gasto</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-129">
              <X className="w-6 h-6 text-red-300 mb-2" data-id="element-130" />
              <p data-id="element-131">no hay límites claros</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-132">
              <X className="w-6 h-6 text-red-300 mb-2" data-id="element-133" />
              <p data-id="element-134">no hay cajones</p>
            </div>
            <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm" data-id="element-135">
              <X className="w-6 h-6 text-red-300 mb-2" data-id="element-136" />
              <p data-id="element-137">no hay estructura</p>
            </div>
          </div>

          <p className="pt-8 italic text-white/80" data-id="element-138">
            "Es como tener toda la ropa tirada en la cama. No es que tengas poca
            ropa. Es que no tienes dónde ordenarla."
          </p>
        </div>
      </Section>

      {/* THE SOLUTION */}
      <Section background="white" data-id="element-139">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-140">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B4332] mb-6" data-id="element-141">
            La solución que funciona incluso cuando estás cansada
          </h2>
          <p className="text-xl text-gray-600 mb-8" data-id="element-142">
            Aquí va la propuesta más humana que te puedo hacer:
          </p>
          <div className="inline-block bg-[#E8F5E9] px-8 py-4 rounded-2xl border border-[#2D6A4F]/20 mb-8" data-id="element-143">
            <p className="text-2xl font-bold text-[#2D6A4F]" data-id="element-144">
              7 días. 10 minutos al día.
            </p>
          </div>
          <p className="text-gray-600" data-id="element-145">
            No un curso eterno. No un Excel que te pide registrar hasta el
            chicle. No una app con 40 categorías. Un plan corto, claro y
            realista... que puedes empezar hoy.
          </p>
        </div>

        <div className="bg-[#2D6A4F] text-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-146">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#52B788] rounded-full blur-3xl opacity-20 -mr-20 -mt-20" data-id="element-147" />

          <div className="relative z-10 text-center" data-id="element-148">
            <p className="text-[#F4A261] font-bold tracking-widest uppercase mb-4" data-id="element-149">
              Presentando
            </p>
            <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight" data-id="element-150">
              Quincena en Verde™
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto mb-8" data-id="element-151">
              Un ebook práctico (Edición LATAM + hispana USA) creado para
              mujeres 20–45 que cobran por quincena/biweekly.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-12 bg-white/10 rounded-2xl p-8 backdrop-blur-sm" data-id="element-152">
              <div className="text-left" data-id="element-153">
                <p className="text-red-200 font-bold mb-4 uppercase text-sm tracking-wider" data-id="element-154">
                  Transforma esto:
                </p>
                <ul className="space-y-2 text-white/80" data-id="element-155">
                  <li className="flex items-center gap-2" data-id="element-156">
                    <X className="w-4 h-4" data-id="element-157" /> ansiedad por dinero
                  </li>
                  <li className="flex items-center gap-2" data-id="element-158">
                    <X className="w-4 h-4" data-id="element-159" /> fugas invisibles
                  </li>
                  <li className="flex items-center gap-2" data-id="element-160">
                    <X className="w-4 h-4" data-id="element-161" /> deuda estancada
                  </li>
                  <li className="flex items-center gap-2" data-id="element-162">
                    <X className="w-4 h-4" data-id="element-163" /> cero ahorro
                  </li>
                  <li className="flex items-center gap-2" data-id="element-164">
                    <X className="w-4 h-4" data-id="element-165" /> cansancio mental
                  </li>
                </ul>
              </div>
              <div className="text-left border-t md:border-t-0 md:border-l border-white/20 pt-6 md:pt-0 md:pl-8" data-id="element-166">
                <p className="text-[#52B788] font-bold mb-4 uppercase text-sm tracking-wider" data-id="element-167">
                  En esto:
                </p>
                <ul className="space-y-2 text-white font-medium" data-id="element-168">
                  <li className="flex items-center gap-2" data-id="element-169">
                    <Check className="w-4 h-4 text-[#52B788]" data-id="element-170" /> claridad
                  </li>
                  <li className="flex items-center gap-2" data-id="element-171">
                    <Check className="w-4 h-4 text-[#52B788]" data-id="element-172" /> control
                  </li>
                  <li className="flex items-center gap-2" data-id="element-173">
                    <Check className="w-4 h-4 text-[#52B788]" data-id="element-174" /> estructura
                  </li>
                  <li className="flex items-center gap-2" data-id="element-175">
                    <Check className="w-4 h-4 text-[#52B788]" data-id="element-176" /> calma
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-12" data-id="element-177">
              <p className="text-white/70 mb-6" data-id="element-178">
                Sin tecnicismos. Sin regaños. Sin castigo.
              </p>
              <Button variant="primary" size="xl" onClick={scrollToPricing} className="shadow-lg shadow-[#F4A261]/20" data-id="element-179">
                Obtener mi guía ahora
              </Button>
            </div>
          </div>
        </div>
      </Section>

      {/* 7 DAYS BREAKDOWN */}
      <Section background="cream" id="how-it-works" data-id="element-180">
        <div className="text-center mb-16" data-id="element-181">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1B4332] mb-4" data-id="element-182">
            Cómo funciona (los 7 días)
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-183">
            Por eso Quincena en Verde™ está diseñado con una regla:{' '}
            <span className="font-bold text-[#2D6A4F]" data-id="element-184">
              baja fricción, sube claridad.
            </span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-185">
          <DayCard index={0} day={1} title="Diagnóstico Express" pain='"No sé en qué se fue todo."' benefit="En 10 minutos tienes un mapa real. No adivinas. No te juzgas." data-id="element-186" />
          <DayCard index={1} day={2} title="Los 7 gastos invisibles" pain='"Son gastitos... no importa."' benefit="Identificas a los culpables reales y cortas 3 rápido. (Delivery, tiendita, suscripciones...)" data-id="element-187" />
          <DayCard index={2} day={3} title="Sistema 3 Sobres + 1" pain="Dinero mezclado = dinero que desaparece" benefit="Tu dinero tiene lugar (por fin). Obligaciones, Vida, Deuda/Ahorro, Emergencias." data-id="element-188" />
          <DayCard index={3} day={4} title="Regla Anti-Antojos 24h" pain="Compras impulsivas que te drenan" benefit="Control sin prohibición. Pausa + reemplazo + límite." data-id="element-189" />
          <DayCard index={4} day={5} title='Plan deuda "mínimo + ataque"' pain="Pagar mínimo y seguir igual" benefit="Ver avance realista sin ahogarte. Atacar estratégicamente." data-id="element-190" />
          <DayCard index={5} day={6} title="Ahorro automático" pain='"Ahorro cuando sobre" (y nunca sobra)' benefit="Empiezas a construir colchón. Cambias identidad a 'yo ahorro'." data-id="element-191" />
          <div className="md:col-span-2 lg:col-span-3" data-id="element-192">
            <DayCard index={6} day={7} title="Ritual de Quincena (15 min)" pain="Recaer cada pago" benefit="Sostener el cambio sin depender de motivación. Mantenimiento simple." data-id="element-193" />
          </div>
        </div>
      </Section>

      {/* METAPHORS */}
      <Section background="white" data-id="element-194">
        <h2 className="text-3xl font-bold text-center text-[#1B4332] mb-12" data-id="element-195">
          Tres metáforas para que lo sientas en el cuerpo
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-196">
          <MetaphorCard index={0} icon="bucket" title="Balde con micro-fugas" description="No te vacías por un hueco enorme... sino por 12 chiquitos diarios." data-id="element-197" />
          <MetaphorCard index={1} icon="closet" title="Clóset sin cajones" description="No es que tengas poca ropa; es que no tienes dónde ordenarla (sobres = cajones)." data-id="element-198" />
          <MetaphorCard index={2} icon="gps" title='GPS vs "yo me lo sé"' description='Creer que "gastas poco" es manejar sin GPS; el diagnóstico te muestra el mapa real.' data-id="element-199" />
        </div>
      </Section>

      {/* WHAT YOU GET */}
      <Section background="green" data-id="element-200">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-[#2D6A4F]/10" data-id="element-201">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-202">
            <div data-id="element-203">
              <h2 className="text-3xl font-bold text-[#1B4332] mb-6" data-id="element-204">
                Lo que obtienes exactamente
              </h2>
              <ul className="space-y-4" data-id="element-205">
                {['Quincena en Verde™ (ebook práctico)', 'Plan de 7 días', '10 minutos al día', 'Plantillas listas para copiar/pegar', 'Aplicable en efectivo o digital', 'Cero tecnicismos, cero regaños'].map((item, i) => <li key={i} className="flex items-center gap-3 text-lg text-gray-700" data-id="element-206">
                    <div className="w-6 h-6 rounded-full bg-[#52B788]/20 flex items-center justify-center flex-shrink-0" data-id="element-207">
                      <Check className="w-4 h-4 text-[#2D6A4F]" data-id="element-208" />
                    </div>
                    {item}
                  </li>)}
              </ul>
              <div className="mt-8 p-4 bg-yellow-50 rounded-lg border border-yellow-100 text-sm text-yellow-800" data-id="element-209">
                <span className="font-bold" data-id="element-210">Aviso importante:</span> Esto es
                educativo y práctico. No reemplaza asesoría financiera
                personalizada.
              </div>
            </div>
            <div className="relative" data-id="element-211">
              <div className="absolute inset-0 bg-[#2D6A4F] rounded-2xl rotate-3 opacity-10" data-id="element-212" />
              <img src="https://cdn.magicpatterns.com/uploads/9WrQcZWLeYMEo1qTCJ92wY/Producto_Principal.png" alt="Ebook Preview" className="relative z-10 w-full rounded-lg shadow-xl" data-id="element-213" />
            </div>
          </div>
        </div>
      </Section>

      {/* WHO IS IT FOR */}
      <Section background="cream" data-id="element-214">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-215">
          <div className="bg-white p-8 rounded-2xl border-t-4 border-[#52B788] shadow-sm" data-id="element-216">
            <h3 className="text-xl font-bold text-[#1B4332] mb-6 flex items-center gap-2" data-id="element-217">
              <Check className="text-[#52B788]" data-id="element-218" /> Es para ti si:
            </h3>
            <ul className="space-y-3 text-gray-700" data-id="element-219">
              <li className="flex gap-2" data-id="element-220">
                <Check className="w-5 h-5 text-[#52B788] flex-shrink-0" data-id="element-221" />{' '}
                cobras por quincena/biweekly
              </li>
              <li className="flex gap-2" data-id="element-222">
                <Check className="w-5 h-5 text-[#52B788] flex-shrink-0" data-id="element-223" /> el
                dinero se te "desaparece" sin una compra grande
              </li>
              <li className="flex gap-2" data-id="element-224">
                <Check className="w-5 h-5 text-[#52B788] flex-shrink-0" data-id="element-225" /> odias
                Excel y lo complejo
              </li>
              <li className="flex gap-2" data-id="element-226">
                <Check className="w-5 h-5 text-[#52B788] flex-shrink-0" data-id="element-227" />{' '}
                quieres control real con un sistema simple
              </li>
            </ul>
          </div>

          <div className="bg-white p-8 rounded-2xl border-t-4 border-gray-300 shadow-sm opacity-80" data-id="element-228">
            <h3 className="text-xl font-bold text-gray-600 mb-6 flex items-center gap-2" data-id="element-229">
              <X className="text-gray-400" data-id="element-230" /> No es para ti si:
            </h3>
            <ul className="space-y-3 text-gray-600" data-id="element-231">
              <li className="flex gap-2" data-id="element-232">
                <X className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-233" /> buscas un
                "milagro" sin cambiar nada
              </li>
              <li className="flex gap-2" data-id="element-234">
                <X className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-235" /> quieres un
                método que requiera horas diarias
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* PRICING SECTION */}
      <Section background="white" id="pricing" data-id="element-236">
        <div className="max-w-5xl mx-auto" data-id="element-237">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-gradient-to-br from-[#2D6A4F] to-[#1B4332] rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden" data-id="element-238">
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#52B788] rounded-full blur-3xl opacity-10 -mr-32 -mt-32" data-id="element-239" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#F4A261] rounded-full blur-3xl opacity-10 -ml-20 -mb-20" data-id="element-240" />

            <div className="relative z-10 space-y-8" data-id="element-241">
              {/* Header */}
              <div className="text-center text-white space-y-4" data-id="element-242">
                <div className="inline-block px-4 py-1.5 bg-[#F4A261] text-[#1B4332] font-bold rounded-full text-sm" data-id="element-243">
                  Oferta Especial
                </div>

                <h2 className="text-4xl md:text-5xl font-black" data-id="element-244">
                  Empieza hoy mismo
                </h2>

                <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-245">
                  Acceso inmediato al ebook completo + plantillas listas para
                  usar
                </p>
              </div>

              {/* Product mockup - moved here */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative max-w-4xl mx-auto" data-id="element-246">
                <img src="https://cdn.magicpatterns.com/uploads/3L17bQo41voWaKuB8WGYTr/Mockup_Producto_Principal.png" alt="Quincena en Verde en múltiples dispositivos - laptop y tablet" className="w-full h-auto rounded-lg" data-id="element-247" />
              </motion.div>

              {/* Pricing and benefits */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start pt-8" data-id="element-248">
                {/* Left - Price */}
                <div className="text-white space-y-3 text-center lg:text-left" data-id="element-249">
                  <div className="inline-flex items-center justify-center lg:justify-start gap-2" data-id="element-250">
                    <span className="text-xl text-white/50 line-through font-semibold" data-id="element-251">
                      $15.94
                    </span>
                    <span className="text-6xl md:text-7xl font-black text-[#F4A261]" data-id="element-252">
                      $8,97
                    </span>
                    <span className="text-2xl font-bold text-white/90" data-id="element-253">
                      USD
                    </span>
                  </div>
                  <p className="text-white/80 text-lg" data-id="element-254">
                    Pago único • Sin suscripciones
                  </p>
                </div>

                {/* Right - Benefits */}
                <div className="space-y-3" data-id="element-255">
                  {['Acceso instantáneo al ebook completo', 'Plan de 7 días paso a paso', 'Plantillas descargables', 'Compatible con móvil, tablet y computadora', 'Sin suscripciones ni cargos ocultos'].map((item, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: i * 0.1
                }} className="flex items-center gap-3 text-white/90" data-id="element-256">
                      <div className="w-6 h-6 rounded-full bg-[#52B788] flex items-center justify-center flex-shrink-0" data-id="element-257">
                        <Check className="w-4 h-4 text-white" data-id="element-258" />
                      </div>
                      {item}
                    </motion.div>)}
                </div>
              </div>

              {/* FREE BONUS SECTION */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="bg-gradient-to-br from-[#F4A261] to-[#E76F51] rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-259">
                {/* Decorative glow */}
                <div className="absolute top-0 left-0 w-full h-full bg-white/5 rounded-2xl" data-id="element-260" />

                <div className="relative z-10" data-id="element-261">
                  {/* Badge */}
                  <div className="flex justify-center mb-4" data-id="element-262">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#E76F51] font-black rounded-full text-sm shadow-lg" data-id="element-263">
                      <span className="text-xl" data-id="element-264">🎁</span>
                      <span data-id="element-265">BONO GRATIS INCLUIDO</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-6" data-id="element-266">
                    Bono 1: Plan Deuda "Mínimo + Ataque"
                  </h3>

                  {/* Bonus Image - Now below title */}
                  <div className="mb-6" data-id="element-267">
                    <img src="https://cdn.magicpatterns.com/uploads/vbpjvhLKzw7QkhaSFbt5Bz/Mockup_BONO1.png" alt="Bono 1: Plan Deuda Mínimo + Ataque" className="w-full h-auto rounded-lg shadow-xl max-w-2xl mx-auto" data-id="element-268" />
                  </div>

                  {/* Bonus Details */}
                  <div className="text-white space-y-4" data-id="element-269">
                    <p className="text-white/90 text-lg text-center" data-id="element-270">
                      Una plantilla práctica para que dejes de pagar "solo el
                      mínimo" y empieces a ver avance real sin ahogarte.
                    </p>

                    <div className="space-y-2 pt-2" data-id="element-271">
                      {['Paga mínimos en todas tus deudas (sin mora ni recargos)', 'Haz un "ataque" constante a una sola deuda hasta cerrarla', 'Cómo elegir tu deuda objetivo (bola de nieve o avalancha)', 'Plantilla para copiar/pegar en Notas o Google Docs', 'Micro-estrategias para crear el ataque aunque sea mini', 'Regla anti-ahogo para no endeudarte otra vez', 'Checklist de control de 1 minuto por periodo'].slice(0, showAllBonus1Features ? undefined : 2).map((feature, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.4 + i * 0.05
                    }} className="flex items-start gap-2 text-sm" data-id="element-272">
                            <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" data-id="element-273" />
                            <span data-id="element-274">{feature}</span>
                          </motion.div>)}

                      <button onClick={() => setShowAllBonus1Features(!showAllBonus1Features)} className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-white text-[#E76F51] rounded-lg font-semibold text-sm hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-275">
                        <span data-id="element-276">
                          {showAllBonus1Features ? 'Ver menos' : 'Ver más'}
                        </span>
                        {showAllBonus1Features ? <ChevronUp className="w-4 h-4" data-id="element-277" /> : <ChevronDown className="w-4 h-4" data-id="element-278" />}
                      </button>
                    </div>

                    <div className="pt-4 border-t border-white/20" data-id="element-279">
                      <p className="text-white/80 text-sm italic text-center" data-id="element-280">
                        Este bono se incluye automáticamente con tu compra. Sin
                        costo adicional.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* BONUS 2 SECTION */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="bg-gradient-to-br from-[#52B788] to-[#2D6A4F] rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-281">
                {/* Decorative glow */}
                <div className="absolute top-0 left-0 w-full h-full bg-white/5 rounded-2xl" data-id="element-282" />

                <div className="relative z-10" data-id="element-283">
                  {/* Badge */}
                  <div className="flex justify-center mb-4" data-id="element-284">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#2D6A4F] font-black rounded-full text-sm shadow-lg" data-id="element-285">
                      <span className="text-xl" data-id="element-286">🎁</span>
                      <span data-id="element-287">BONO 2 GRATIS INCLUIDO</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-6" data-id="element-288">
                    Bono 2: Plan Deuda "Mínimo + Ataque"
                  </h3>

                  {/* Bonus Image - Now below title */}
                  <div className="mb-6" data-id="element-289">
                    <img src="https://cdn.magicpatterns.com/uploads/saVb9qoCmc8JQngEydXtJD/Mockup_BONO2.png" alt="Bono 2: Plan Deuda Mínimo + Ataque - método ultra simple" className="w-full h-auto rounded-lg shadow-xl max-w-2xl mx-auto" data-id="element-290" />
                  </div>

                  {/* Bonus Details */}
                  <div className="text-white space-y-4" data-id="element-291">
                    <p className="text-white/90 text-lg text-center" data-id="element-292">
                      Método ultra simple + plantilla en moneda global. Un
                      sistema práctico para salir de deudas sin ahogarte ni
                      depender de motivación.
                    </p>

                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4" data-id="element-293">
                      <p className="text-white/95 text-sm" data-id="element-294">
                        En vez de quedarte atrapado pagando solo el mínimo (y
                        viendo que el saldo casi no baja), este plan te da una
                        estrategia clara: pagas los mínimos en todas tus deudas
                        para mantener estabilidad (sin mora, recargos ni daño al
                        historial) y usas tu dinero extra para atacar una sola
                        deuda hasta cerrarla.
                      </p>
                    </div>

                    <div className="space-y-2 pt-2" data-id="element-295">
                      {['Cómo elegir tu deuda objetivo: Bola de Nieve (cierres rápidos y motivación) o Avalancha (más eficiente y menos intereses)', 'Plantilla ultra simple para copiar/pegar (Notas/Google Docs/WhatsApp) con tu foto real, lista de deudas y plan de pago por periodo', 'Micro-estrategias para crear ataque aunque sea pequeño (recorte invisible, ataque por bloques, simbólico y fondo "por si las dudas")', 'Regla anti-ahogo + protocolo "si solo pude pagar mínimos" (sin culpa)', 'Checklist de 1 minuto por periodo para medir avance y sostener consistencia'].slice(0, showAllBonus2Features ? undefined : 2).map((feature, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.5 + i * 0.05
                    }} className="flex items-start gap-2 text-sm" data-id="element-296">
                            <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" data-id="element-297" />
                            <span data-id="element-298">{feature}</span>
                          </motion.div>)}

                      <button onClick={() => setShowAllBonus2Features(!showAllBonus2Features)} className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-white text-[#2D6A4F] rounded-lg font-semibold text-sm hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-299">
                        <span data-id="element-300">
                          {showAllBonus2Features ? 'Ver menos' : 'Ver más'}
                        </span>
                        {showAllBonus2Features ? <ChevronUp className="w-4 h-4" data-id="element-301" /> : <ChevronDown className="w-4 h-4" data-id="element-302" />}
                      </button>
                    </div>

                    <div className="pt-4 border-t border-white/20" data-id="element-303">
                      <p className="text-white font-semibold text-sm text-center" data-id="element-304">
                        Resultado: estabilidad + progreso real, con un efecto
                        dominó que acelera cada vez que cierras una deuda.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* BONUS 3 SECTION */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="bg-gradient-to-br from-[#1B4332] to-[#0F2419] rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-305">
                {/* Decorative glow */}
                <div className="absolute top-0 left-0 w-full h-full bg-white/5 rounded-2xl" data-id="element-306" />

                <div className="relative z-10" data-id="element-307">
                  {/* Badge */}
                  <div className="flex justify-center mb-4" data-id="element-308">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#1B4332] font-black rounded-full text-sm shadow-lg" data-id="element-309">
                      <span className="text-xl" data-id="element-310">🎁</span>
                      <span data-id="element-311">BONO 3 GRATIS INCLUIDO</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-6" data-id="element-312">
                    Bono 3: Guía Express "Cancela Suscripciones Fantasma"
                  </h3>

                  {/* Bonus Image - Now below title */}
                  <div className="mb-6" data-id="element-313">
                    <img src="https://cdn.magicpatterns.com/uploads/8T1NNa82xiDV1EeRaJdEhR/Mockup_BONO3.png" alt="Bono 3: Guía Express Cancela Suscripciones Fantasma" className="w-full h-auto rounded-lg shadow-xl max-w-2xl mx-auto" data-id="element-314" />
                  </div>

                  {/* Bonus Details */}
                  <div className="text-white space-y-4" data-id="element-315">
                    <p className="text-white/90 text-lg text-center" data-id="element-316">
                      Una guía rápida de 20 minutos para recuperar dinero
                      "oculto" que se te va en cobros pequeños y automáticos.
                    </p>

                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4" data-id="element-317">
                      <p className="text-white/95 text-sm" data-id="element-318">
                        Apps, membresías, trials y servicios olvidados que
                        drenan tu cuenta sin que lo notes. En solo 20 minutos,
                        sigues un paso a paso para detectar cargos recurrentes
                        en tu banco, confirmarlos en tu correo, revisarlos desde
                        iOS/Android y decidir qué hacer con una regla simple de
                        3 preguntas.
                      </p>
                    </div>

                    <div className="space-y-2 pt-2" data-id="element-319">
                      {['Método express para encontrar suscripciones repetidas en 60–90 días de movimientos', 'Palabras clave para rastrear cobros y renovaciones en el email', '3 caminos de acción: cancelar, bajar de plan o pausar 30 días', 'Plantilla de auditoría para anotar suscripciones y calcular tu ahorro mensual y anual', '"Trampas comunes" (trials, duplicadas, cambios de tarjeta) para que no te vuelvan a cobrar', 'Sistema de revisión trimestral para mantener el control sin esfuerzo'].slice(0, showAllBonus3Features ? undefined : 2).map((feature, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.6 + i * 0.05
                    }} className="flex items-start gap-2 text-sm" data-id="element-320">
                            <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" data-id="element-321" />
                            <span data-id="element-322">{feature}</span>
                          </motion.div>)}

                      <button onClick={() => setShowAllBonus3Features(!showAllBonus3Features)} className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-white text-[#1B4332] rounded-lg font-semibold text-sm hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-323">
                        <span data-id="element-324">
                          {showAllBonus3Features ? 'Ver menos' : 'Ver más'}
                        </span>
                        {showAllBonus3Features ? <ChevronUp className="w-4 h-4" data-id="element-325" /> : <ChevronDown className="w-4 h-4" data-id="element-326" />}
                      </button>
                    </div>

                    <div className="pt-4 border-t border-white/20" data-id="element-327">
                      <p className="text-white font-semibold text-sm text-center" data-id="element-328">
                        Resultado: más aire en tu quincena, menos estrés y la
                        sensación real de "subirte el sueldo" recortando fugas
                        invisibles.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* BONUS4 SECTION */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.6
            }} className="bg-gradient-to-br from-[#E76F51] to-[#D64933] rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-329">
                {/* Decorative glow */}
                <div className="absolute top-0 left-0 w-full h-full bg-white/5 rounded-2xl" data-id="element-330" />

                <div className="relative z-10" data-id="element-331">
                  {/* Badge */}
                  <div className="flex justify-center mb-4" data-id="element-332">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#E76F51] font-black rounded-full text-sm shadow-lg" data-id="element-333">
                      <span className="text-xl" data-id="element-334">🎁</span>
                      <span data-id="element-335">BONO 4 GRATIS INCLUIDO</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-6" data-id="element-336">
                    Bono 4: Reto 72h "Cero Fugas Invisibles"
                  </h3>

                  {/* Bonus Image - Now below title */}
                  <div className="mb-6" data-id="element-337">
                    <img src="https://cdn.magicpatterns.com/uploads/dX18b6FB25ndAXmxLFc1qH/Mockup_BONO4.png" alt="Bono 4: Reto 72h Cero Fugas Invisibles" className="w-full h-auto rounded-lg shadow-xl max-w-2xl mx-auto" data-id="element-338" />
                  </div>

                  {/* Bonus Details */}
                  <div className="text-white space-y-4" data-id="element-339">
                    <p className="text-white/90 text-lg text-center" data-id="element-340">
                      Un mini-desafío de 3 días diseñado para darte tu primer
                      logro rápido y romper la trampa de "empiezo después".
                    </p>

                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4" data-id="element-341">
                      <p className="text-white/95 text-sm" data-id="element-342">
                        En 72 horas, aprendes a cortar las fugas invisibles
                        (tiendita, domicilios, Uber por costumbre, compras
                        impulsivas, recargos evitables y suscripciones) sin
                        Excel ni sacrificios extremos, con reglas claras y un
                        plan súper práctico.
                      </p>
                    </div>

                    <div className="space-y-2 pt-2" data-id="element-343">
                      {['Reglas simples (no es "cero gasto", es cero fugas) + Plan B si te da ansiedad', 'Preparación en 5 minutos: lista de reemplazos, pausa de 24h y "zona prohibida" (solo una)', 'Guía día por día: Día 1 cierre de llaves, Día 2 cero impulso (wishlist + pausa 24h), Día 3 mini auditoría + cálculo del dinero salvado', 'Protocolo anti-culpa si fallas (ajuste y continúas, sin reiniciar)', 'Plantilla express de tracking + "premio inteligente" para reforzar el hábito'].slice(0, showAllBonus4Features ? undefined : 2).map((feature, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.7 + i * 0.05
                    }} className="flex items-start gap-2 text-sm" data-id="element-344">
                            <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" data-id="element-345" />
                            <span data-id="element-346">{feature}</span>
                          </motion.div>)}

                      <button onClick={() => setShowAllBonus4Features(!showAllBonus4Features)} className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-white text-[#E76F51] rounded-lg font-semibold text-sm hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-347">
                        <span data-id="element-348">
                          {showAllBonus4Features ? 'Ver menos' : 'Ver más'}
                        </span>
                        {showAllBonus4Features ? <ChevronUp className="w-4 h-4" data-id="element-349" /> : <ChevronDown className="w-4 h-4" data-id="element-350" />}
                      </button>
                    </div>

                    <div className="pt-4 border-t border-white/20" data-id="element-351">
                      <p className="text-white font-semibold text-sm text-center" data-id="element-352">
                        Resultado: sensación inmediata de control, dinero que
                        dejas de "gotear" sin darte cuenta y la confianza de "si
                        pude 3 días, puedo 7… y luego una quincena completa".
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* BONUS 5 SECTION */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.7
            }} className="bg-gradient-to-br from-[#3B9B9B] to-[#2A7272] rounded-2xl p-6 md:p-8 relative overflow-hidden" data-id="element-353">
                {/* Decorative glow */}
                <div className="absolute top-0 left-0 w-full h-full bg-white/5 rounded-2xl" data-id="element-354" />

                <div className="relative z-10" data-id="element-355">
                  {/* Badge */}
                  <div className="flex justify-center mb-4" data-id="element-356">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#3B9B9B] font-black rounded-full text-sm shadow-lg" data-id="element-357">
                      <span className="text-xl" data-id="element-358">🎁</span>
                      <span data-id="element-359">BONO 5 GRATIS INCLUIDO</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl md:text-3xl font-black text-white text-center mb-6" data-id="element-360">
                    Bono 5: Kit "Vida Variable"
                  </h3>

                  {/* Bonus Image - Now below title */}
                  <div className="mb-6" data-id="element-361">
                    <img src="https://cdn.magicpatterns.com/uploads/8HmanyR6Xr6tqMuGp2GkUE/Mockup_BONO5.png" alt="Bono 5: Kit Vida Variable" className="w-full h-auto rounded-lg shadow-xl max-w-2xl mx-auto" data-id="element-362" />
                  </div>

                  {/* Bonus Details */}
                  <div className="text-white space-y-4" data-id="element-363">
                    <p className="text-white/90 text-lg text-center" data-id="element-364">
                      Un kit práctico para que el gasto variable deje de comerse
                      tu quincena sin que lo notes.
                    </p>

                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4" data-id="element-365">
                      <p className="text-white/95 text-sm" data-id="element-366">
                        En vez de "prohibirte la vida", configuras topes humanos
                        por categoría (tiendita, snacks, domicilios, Uber,
                        antojos y planes sociales) que se deciden antes (con la
                        cabeza fría), para que en el momento del antojo ya
                        tengas "paredes" que te protejan sin culpa.
                      </p>
                    </div>

                    <div className="space-y-2 pt-2" data-id="element-367">
                      {['Cómo crear 3 a 5 categorías simples y aplicar topes diarios, semanales o quincenales', '3 formatos de límite: por frecuencia, por monto (moneda global) o por fichas (gamificado)', 'Un "Sí permitido" por categoría (para que no rebotes) + Plan B estratégico cuando llegas al límite', '3 modos según tu quincena: Modo Verde, Modo Ajuste y Modo Rescate (reset corto)', 'Plantilla lista para copiar/pegar y métodos para trackear sin Excel (notas, fichas o sobres digitales)'].slice(0, showAllBonus5Features ? undefined : 2).map((feature, i) => <motion.div key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} whileInView={{
                      opacity: 1,
                      x: 0
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 0.3,
                      delay: 0.8 + i * 0.05
                    }} className="flex items-start gap-2 text-sm" data-id="element-368">
                            <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" data-id="element-369" />
                            <span data-id="element-370">{feature}</span>
                          </motion.div>)}

                      <button onClick={() => setShowAllBonus5Features(!showAllBonus5Features)} className="flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-white text-[#3B9B9B] rounded-lg font-semibold text-sm hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-371">
                        <span data-id="element-372">
                          {showAllBonus5Features ? 'Ver menos' : 'Ver más'}
                        </span>
                        {showAllBonus5Features ? <ChevronUp className="w-4 h-4" data-id="element-373" /> : <ChevronDown className="w-4 h-4" data-id="element-374" />}
                      </button>
                    </div>

                    <div className="pt-4 border-t border-white/20" data-id="element-375">
                      <p className="text-white font-semibold text-sm text-center" data-id="element-376">
                        Resultado: menos ansiedad, más claridad y una quincena
                        más predecible, porque lo variable por fin tiene límites
                        "humanos" que se sostienen.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* CTA Button */}
              <div className="pt-6 max-w-2xl mx-auto" data-id="element-377">
                <Button variant="primary" size="xl" fullWidth onClick={handleCheckout} className="shadow-2xl shadow-[#F4A261]/30 hover:shadow-[#F4A261]/50 transition-shadow" data-id="element-378">
                  Obtener mi guía ahora
                </Button>
                <p className="text-center text-white/60 text-sm mt-4" data-id="element-379">
                  🔒 Pago seguro • Acceso inmediato
                </p>
              </div>
            </div>
          </motion.div>

          {/* Trust badges */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto" data-id="element-380">
            {[{
            text: 'Pago único',
            delay: 0
          }, {
            text: 'Sin suscripciones',
            delay: 0.1
          }, {
            text: 'Acceso de por vida',
            delay: 0.2
          }].map((badge, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4 + badge.delay
          }} whileHover={{
            scale: 1.05,
            transition: {
              duration: 0.2
            }
          }} className="group relative bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-xl hover:border-[#52B788]/30 transition-all duration-300 cursor-default" data-id="element-381">
                {/* Decorative gradient background on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#52B788]/5 to-[#2D6A4F]/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-382" />

                <div className="relative z-10 flex flex-col items-center gap-3 text-center" data-id="element-383">
                  <motion.div initial={{
                scale: 1
              }} whileHover={{
                scale: 1.1,
                rotate: 5
              }} transition={{
                duration: 0.3
              }} className="w-12 h-12 rounded-full bg-[#52B788]/10 flex items-center justify-center group-hover:bg-[#52B788]/20 transition-colors duration-300" data-id="element-384">
                    <Check className="w-6 h-6 text-[#52B788] group-hover:text-[#2D6A4F] transition-colors duration-300" data-id="element-385" />
                  </motion.div>
                  <span className="font-semibold text-gray-700 group-hover:text-[#2D6A4F] transition-colors duration-300" data-id="element-386">
                    {badge.text}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </Section>

      {/* PROMISE & FINAL CTA */}
      <Section background="dark" id="pricing" className="text-center" data-id="element-387">
        <div className="max-w-3xl mx-auto" data-id="element-388">
          <h2 className="text-3xl md:text-4xl font-bold text-[#F4A261] mb-8" data-id="element-389">
            La promesa (sin humo)
          </h2>
          <p className="text-xl text-white mb-8" data-id="element-390">
            En 7 días vas a identificar tus fugas invisibles, instalar un
            sistema simple sin Excel, y sentir control real sobre tu
            quincena/biweekly... incluso si antes siempre recaías.
          </p>
          <p className="text-lg text-white/70 mb-12" data-id="element-391">
            No porque te vuelvas "perfecta". Sino porque tu dinero deja de estar
            mezclado.
          </p>

          <div className="bg-white text-[#1B4332] p-8 md:p-12 rounded-3xl shadow-2xl relative overflow-hidden" data-id="element-392">
            <div className="relative z-10" data-id="element-393">
              <h3 className="text-2xl md:text-3xl font-bold mb-6" data-id="element-394">
                ¿Qué te cuesta más?
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left" data-id="element-395">
                <div className="p-4 bg-red-50 rounded-xl border border-red-100" data-id="element-396">
                  <p className="font-semibold text-red-800" data-id="element-397">Opción A</p>
                  <p className="text-gray-700" data-id="element-398">
                    Seguir como estás... repitiendo el ciclo y pagando con
                    ansiedad.
                  </p>
                </div>
                <div className="p-4 bg-[#E8F5E9] rounded-xl border border-[#52B788]/30" data-id="element-399">
                  <p className="font-semibold text-[#2D6A4F]" data-id="element-400">Opción B</p>
                  <p className="text-gray-700" data-id="element-401">
                    Darte 10 minutos al día por una semana... para por fin ver
                    claridad y estructura.
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-402">
                <p className="text-lg font-medium" data-id="element-403">
                  Si cobras por quincena/biweekly y ya te cansaste de llegar en
                  rojo...
                </p>

                <Button fullWidth size="xl" onClick={scrollToPricing} className="shadow-xl shadow-[#F4A261]/30 animate-pulse-slow" data-id="element-404">
                  ✅ Consigue Quincena en Verde™ ahora
                </Button>

                <p className="text-sm text-gray-500" data-id="element-405">
                  Empieza el Día 1 en los próximos 10 minutos.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-white/60 text-sm" data-id="element-406">
            <p data-id="element-407">
              Tu quincena no tiene que doler. Tu dinero no tiene que
              desaparecer.
            </p>
            <p className="mt-2" data-id="element-408">
              A veces, lo único que faltaba era esto: un sistema simple que sí
              se sostiene.
            </p>
          </div>
        </div>
      </Section>

      <footer className="bg-[#1B4332] border-t border-white/10 py-8 text-center text-white/40 text-sm" data-id="element-409">
        <p data-id="element-410">
          © {new Date().getFullYear()} Quincena en Verde™. Todos los derechos
          reservados.
        </p>
      </footer>
    </main>;
}